import { API_BASE_URL } from "src/config/apiBase";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  GraduationCap,
  ClipboardCheck,
  ArrowRight,
  Users,
  UserCheck,
  Layers,
  CalendarCheck,
  AlertCircle,
} from "lucide-react";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";
import { isInFilter } from "../utils/dateFilter";
import { shouldShowSkatesDashboard } from "../../utils/skatesCategory";

const url = API_BASE_URL;
const FILTER_TABS = ["Today", "Yesterday", "Weekly", "Monthly", "All Time", "Custom Date"];

function localISODate(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getFilterLabel(filter) {
  if (typeof filter === "string") return filter;
  if (filter?.type === "Custom Date" && filter.from && filter.to) {
    return `${filter.from} to ${filter.to}`;
  }
  return "Selected period";
}

function getFilterDateRange(filter) {
  const today = new Date();
  const todayStr = localISODate(today);

  if (typeof filter === "string") {
    switch (filter) {
      case "Today":
        return { from: todayStr, to: todayStr };
      case "Yesterday": {
        const y = new Date(today);
        y.setDate(y.getDate() - 1);
        const ys = localISODate(y);
        return { from: ys, to: ys };
      }
      case "Weekly": {
        const start = new Date(today);
        start.setDate(start.getDate() - 6);
        return { from: localISODate(start), to: todayStr };
      }
      case "Monthly": {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        return { from: localISODate(start), to: todayStr };
      }
      case "All Time":
        return { from: "2000-01-01", to: todayStr };
      default:
        return { from: todayStr, to: todayStr };
    }
  }

  if (filter?.type === "Custom Date" && filter.from && filter.to) {
    return { from: filter.from, to: filter.to };
  }

  return { from: todayStr, to: todayStr };
}

const SummaryMetric = ({ label, value, hint, tone = "default" }) => {
  const toneClass =
    tone === "success"
      ? "text-lime-300"
      : tone === "warn"
        ? "text-amber-300"
        : tone === "danger"
          ? "text-rose-300"
          : "text-ink";

  return (
    <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 min-w-[118px]">
      <p className="text-[11px] uppercase tracking-wide text-ink-muted font-semibold">{label}</p>
      <p className={`text-lg font-bold ${toneClass}`}>{value}</p>
      {hint ? <p className="text-[11px] text-ink-muted mt-0.5">{hint}</p> : null}
    </div>
  );
};

const SummaryDateFilter = ({ value, onChange }) => {
  const [activeTab, setActiveTab] = useState(
    typeof value === "string" ? value : value?.type === "Custom Date" ? "Custom Date" : "Today"
  );
  const [showCustom, setShowCustom] = useState(activeTab === "Custom Date");
  const [tempRange, setTempRange] = useState({
    from: value?.from || "",
    to: value?.to || "",
  });

  const handleTabClick = (tab) => {
    if (tab === "Custom Date") {
      setActiveTab("Custom Date");
      setShowCustom(true);
      return;
    }
    setActiveTab(tab);
    setShowCustom(false);
    onChange(tab);
  };

  const handleApplyCustom = () => {
    if (!tempRange.from || !tempRange.to || tempRange.from > tempRange.to) return;
    onChange({ type: "Custom Date", from: tempRange.from, to: tempRange.to });
    setShowCustom(false);
  };

  return (
    <div className="w-full sm:w-auto sm:min-w-[280px]">
      <div className="flex items-center gap-1 dash-filter-scroll rounded-xl border border-line bg-surface-2 p-1">
        {FILTER_TABS.map((tab) => {
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => handleTabClick(tab)}
              className={`shrink-0 rounded-lg px-2.5 py-1.5 text-[11px] sm:text-xs font-semibold transition-colors whitespace-nowrap ${
                isActive
                  ? "bg-primary text-white shadow-sm"
                  : "text-ink-muted hover:bg-white/10 hover:text-ink"
              }`}
            >
              {tab}
            </button>
          );
        })}
      </div>

      {showCustom && (
        <div className="mt-2 rounded-xl border border-dashed border-line-strong bg-surface-2 p-2.5">
          <div className="flex flex-col sm:flex-row gap-2">
            <label className="flex flex-col gap-1 text-[11px] font-semibold text-ink-secondary flex-1">
              From
              <input
                type="date"
                value={tempRange.from}
                onChange={(e) => setTempRange((prev) => ({ ...prev, from: e.target.value }))}
                className="rounded-lg border border-line bg-surface text-ink px-2 py-1.5 text-xs outline-none focus:border-primary [color-scheme:dark]"
              />
            </label>
            <label className="flex flex-col gap-1 text-[11px] font-semibold text-ink-secondary flex-1">
              To
              <input
                type="date"
                value={tempRange.to}
                onChange={(e) => setTempRange((prev) => ({ ...prev, to: e.target.value }))}
                className="rounded-lg border border-line bg-surface text-ink px-2 py-1.5 text-xs outline-none focus:border-primary [color-scheme:dark]"
              />
            </label>
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowCustom(false);
                if (typeof value === "string") setActiveTab(value);
              }}
              className="rounded-lg border border-line px-3 py-1 text-xs font-semibold text-ink-secondary hover:bg-white/10"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApplyCustom}
              disabled={!tempRange.from || !tempRange.to || tempRange.from > tempRange.to}
              className="rounded-lg bg-primary px-3 py-1 text-xs font-semibold text-white hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryColumn = ({ title, icon: Icon, tone, children, onViewMore, viewLabel, scrollTheme = "admission" }) => (
  <div className={`rounded-xl border p-3 sm:p-4 bg-gradient-to-br ${tone}`}>
    <div className="flex items-center justify-between gap-2 mb-3">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-ink shadow-sm">
          <Icon size={16} />
        </div>
        <h3 className="text-sm sm:text-base font-bold text-ink">{title}</h3>
      </div>
      <button
        type="button"
        onClick={onViewMore}
        className="inline-flex items-center gap-1 text-xs font-semibold text-brand-cyan hover:text-cyan-300"
      >
        {viewLabel}
        <ArrowRight size={12} />
      </button>
    </div>
    <div className={`flex gap-2 dash-metric-scroll dash-metric-scroll--${scrollTheme}`}>{children}</div>
  </div>
);

export default function SkatesAcademySummary() {
  const navigate = useNavigate();
  const user = getDecryptedUserFromCookie("gta_mainstateuser");
  const token = decryptToken("accessToken") || "";
  const [filter, setFilter] = useState("Today");
  const [loading, setLoading] = useState(true);
  const [admissions, setAdmissions] = useState([]);
  const [batches, setBatches] = useState([]);
  const [attendanceToday, setAttendanceToday] = useState({
    present: 0,
    absent: 0,
    late: 0,
    batchesMarked: 0,
    batchesUnmarked: 0,
  });
  const [attendancePeriod, setAttendancePeriod] = useState({
    present: 0,
    absent: 0,
    late: 0,
    markedDays: 0,
  });

  const visible = shouldShowSkatesDashboard(user);

  const fetchSummary = useCallback(async () => {
    if (!visible || !user?.main_id || !token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    const today = localISODate();
    const { from, to } = getFilterDateRange(filter);

    try {
      const [admissionRes, batchRes] = await Promise.all([
        fetch(`${url}/admission/alladmin/${user.main_id}`, { headers }),
        fetch(`${url}/admission/batches`, { headers }),
      ]);

      const admissionJson = await admissionRes.json();
      const batchJson = await batchRes.json();

      const admissionRows = Array.isArray(admissionJson?.user) ? admissionJson.user : [];
      const batchRows = Array.isArray(batchJson?.batches) ? batchJson.batches : [];

      setAdmissions(admissionRows);
      setBatches(batchRows);

      const activeBatches = batchRows.filter(
        (b) => String(b.status || "").trim().toLowerCase() === "active"
      );

      const sheetResults = await Promise.all(
        activeBatches.map(async (batch) => {
          try {
            const qs = new URLSearchParams({ batch_id: String(batch.id), date: today });
            const res = await fetch(`${url}/admission/student-attendance/sheet?${qs}`, { headers });
            const data = await res.json();
            if (!res.ok || data.isError) return null;
            return {
              batchId: batch.id,
              studentCount: data.students?.length || batch.studentCount || 0,
              savedRowCount: data.savedRowCount || 0,
              counts: data.counts || { Present: 0, Absent: 0, Late: 0 },
            };
          } catch {
            return null;
          }
        })
      );

      const todayAgg = { present: 0, absent: 0, late: 0, batchesMarked: 0, batchesUnmarked: 0 };
      sheetResults.filter(Boolean).forEach((sheet) => {
        const hasStudents = sheet.studentCount > 0;
        if (!hasStudents) return;

        if (sheet.savedRowCount > 0) {
          todayAgg.batchesMarked += 1;
          todayAgg.present += sheet.counts.Present || 0;
          todayAgg.absent += sheet.counts.Absent || 0;
          todayAgg.late += sheet.counts.Late || 0;
        } else {
          todayAgg.batchesUnmarked += 1;
        }
      });
      setAttendanceToday(todayAgg);

      const historyResults = await Promise.all(
        activeBatches.map(async (batch) => {
          try {
            const qs = new URLSearchParams({
              batch_id: String(batch.id),
              from,
              to,
            });
            const res = await fetch(
              `${url}/admission/student-attendance/history/summary?${qs}`,
              { headers }
            );
            const data = await res.json();
            if (!res.ok || data.isError) return [];
            return Array.isArray(data.days) ? data.days : [];
          } catch {
            return [];
          }
        })
      );

      const periodAgg = { present: 0, absent: 0, late: 0, markedDays: 0 };
      historyResults.flat().forEach((day) => {
        periodAgg.present += day.present || 0;
        periodAgg.absent += day.absent || 0;
        periodAgg.late += day.late || 0;
        if ((day.total || 0) > 0) periodAgg.markedDays += 1;
      });
      setAttendancePeriod(periodAgg);
    } catch (error) {
      console.error("Skates academy summary fetch failed:", error);
    } finally {
      setLoading(false);
    }
  }, [filter, token, user?.main_id, visible]);

  useEffect(() => {
    void fetchSummary();
  }, [fetchSummary]);

  const summary = useMemo(() => {
    const periodAdmissions = admissions.filter((row) => isInFilter(row.createdAt, filter));
    const activeStudents = admissions.filter((row) => row.status === "Active").length;
    const paidStudents = admissions.filter((row) => row.status === "Paid").length;
    const deactiveStudents = admissions.filter((row) => row.status === "Deactive").length;
    const failedStudents = admissions.filter((row) => row.status === "Failed").length;
    const unbatched = admissions.filter((row) => !row.batch_id).length;

    const activeBatches = batches.filter(
      (b) => String(b.status || "").trim().toLowerCase() === "active"
    );
    const enrolledInBatches = activeBatches.reduce(
      (sum, b) => sum + (Number(b.studentCount) || 0),
      0
    );
    const atCapacity = activeBatches.filter((b) => {
      const max = Number(b.max_students) || 0;
      const count = Number(b.studentCount) || 0;
      return max > 0 && count >= max;
    }).length;

    const todayTotal =
      attendanceToday.present + attendanceToday.absent + attendanceToday.late;
    const todayRate =
      todayTotal > 0
        ? `${Math.round((attendanceToday.present / todayTotal) * 100)}%`
        : "—";

    const periodTotal =
      attendancePeriod.present + attendancePeriod.absent + attendancePeriod.late;
    const periodRate =
      periodTotal > 0
        ? `${Math.round((attendancePeriod.present / periodTotal) * 100)}%`
        : "—";

    const centers = new Set(
      admissions.map((row) => String(row.center || "").trim()).filter(Boolean)
    ).size;

    return {
      filterLabel: getFilterLabel(filter),
      admission: {
        newEnrollments: periodAdmissions.length,
        total: admissions.length,
        active: activeStudents,
        paid: paidStudents,
        deactive: deactiveStudents,
        failed: failedStudents,
        unbatched,
        activeBatches: activeBatches.length,
        enrolledInBatches,
        atCapacity,
        centers,
      },
      attendance: {
        todayPresent: attendanceToday.present,
        todayAbsent: attendanceToday.absent,
        todayLate: attendanceToday.late,
        todayRate,
        batchesMarked: attendanceToday.batchesMarked,
        batchesUnmarked: attendanceToday.batchesUnmarked,
        periodPresent: attendancePeriod.present,
        periodAbsent: attendancePeriod.absent,
        periodLate: attendancePeriod.late,
        periodRate,
        markedDays: attendancePeriod.markedDays,
      },
    };
  }, [admissions, attendancePeriod, attendanceToday, batches, filter]);

  if (!visible) return null;

  if (loading) {
    return (
      <div className="rounded-xl border border-brand-indigo/20 bg-brand-indigo/10 px-4 py-5 text-sm text-ink-secondary animate-pulse">
        Loading academy summary...
      </div>
    );
  }

  return (
    <section className="rounded-2xl border border-line bg-gradient-to-br from-brand-indigo/10 to-brand-cyan/10 p-3 sm:p-4 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base sm:text-lg font-bold text-ink">Academy Summary</h2>
          <p className="text-xs sm:text-sm text-ink-muted">
            Admissions and student attendance for your skating academy — {summary.filterLabel}
          </p>
        </div>
        <SummaryDateFilter value={filter} onChange={setFilter} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <SummaryColumn
          title="Admission"
          icon={GraduationCap}
          tone="from-brand-indigo/10 to-brand-violet/10 border-brand-indigo/20"
          scrollTheme="admission"
          onViewMore={() => navigate("/admission")}
          viewLabel="View Admissions"
        >
          <SummaryMetric
            label="New Enrollments"
            value={summary.admission.newEnrollments}
            hint="In selected period"
          />
          <SummaryMetric label="Total Students" value={summary.admission.total} />
          <SummaryMetric
            label="Active"
            value={summary.admission.active}
            tone="success"
          />
          <SummaryMetric label="Paid" value={summary.admission.paid} tone="success" />
          <SummaryMetric
            label="Deactive"
            value={summary.admission.deactive}
            tone={summary.admission.deactive > 0 ? "warn" : "default"}
          />
          <SummaryMetric
            label="No Batch"
            value={summary.admission.unbatched}
            tone={summary.admission.unbatched > 0 ? "warn" : "default"}
            hint="Need batch assignment"
          />
          <SummaryMetric
            label="Active Batches"
            value={summary.admission.activeBatches}
          />
          <SummaryMetric
            label="In Batches"
            value={summary.admission.enrolledInBatches}
            hint="Across active batches"
          />
        </SummaryColumn>

        <SummaryColumn
          title="Attendance"
          icon={ClipboardCheck}
          tone="from-brand-cyan/10 to-sky-500/10 border-brand-cyan/20"
          scrollTheme="attendance"
          onViewMore={() => navigate("/student-attendance")}
          viewLabel="Mark Attendance"
        >
          <SummaryMetric
            label="Today Present"
            value={summary.attendance.todayPresent}
            tone="success"
            hint="Saved marks today"
          />
          <SummaryMetric
            label="Today Absent"
            value={summary.attendance.todayAbsent}
            tone={summary.attendance.todayAbsent > 0 ? "danger" : "default"}
          />
          <SummaryMetric
            label="Today Late"
            value={summary.attendance.todayLate}
            tone={summary.attendance.todayLate > 0 ? "warn" : "default"}
          />
          <SummaryMetric
            label="Today Rate"
            value={summary.attendance.todayRate}
            hint="Present share today"
          />
          <SummaryMetric
            label="Period Present"
            value={summary.attendance.periodPresent}
            tone="success"
            hint="In selected period"
          />
          <SummaryMetric
            label="Period Absent"
            value={summary.attendance.periodAbsent}
            tone={summary.attendance.periodAbsent > 0 ? "danger" : "default"}
          />
          <SummaryMetric
            label="Period Rate"
            value={summary.attendance.periodRate}
            hint="Present share in period"
          />
          <SummaryMetric
            label="Unmarked Batches"
            value={summary.attendance.batchesUnmarked}
            tone={summary.attendance.batchesUnmarked > 0 ? "danger" : "default"}
            hint="Today, not saved yet"
          />
        </SummaryColumn>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-ink-secondary">
        <div className="flex items-start gap-2 rounded-lg bg-white/5 border border-line px-3 py-2">
          <Users size={14} className="text-brand-violet mt-0.5 shrink-0" />
          <span>
            {summary.admission.unbatched > 0
              ? `${summary.admission.unbatched} student(s) are not assigned to a batch. Assign them from Admissions or Batches.`
              : `All students are assigned to batches across ${summary.admission.centers} center(s).`}
          </span>
        </div>
        <div className="flex items-start gap-2 rounded-lg bg-white/5 border border-line px-3 py-2">
          <CalendarCheck size={14} className="text-brand-cyan mt-0.5 shrink-0" />
          <span>
            {summary.attendance.batchesUnmarked > 0
              ? `${summary.attendance.batchesUnmarked} batch(es) have no attendance saved for today. Open Attendance to mark them.`
              : summary.attendance.batchesMarked > 0
                ? `Today's attendance is marked for ${summary.attendance.batchesMarked} batch(es).`
                : "No active batches with students to mark today."}
          </span>
        </div>
        <div className="flex items-start gap-2 rounded-lg bg-white/5 border border-line px-3 py-2">
          <AlertCircle size={14} className="text-amber-400 mt-0.5 shrink-0" />
          <span>
            {summary.admission.atCapacity > 0
              ? `${summary.admission.atCapacity} batch(es) are at full capacity. Review batch limits before new admissions.`
              : summary.admission.deactive > 0
                ? `${summary.admission.deactive} deactive enrollment(s) may need fee follow-up or reactivation.`
                : "Batch capacity looks healthy. Keep tracking daily attendance in the selected period."}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => navigate("/admission")}
          className="inline-flex items-center gap-1.5 rounded-lg border border-brand-indigo/30 bg-brand-indigo/10 px-3 py-1.5 text-xs font-semibold text-indigo-200 hover:bg-brand-indigo/20"
        >
          <UserCheck size={14} />
          Admissions
        </button>
        <button
          type="button"
          onClick={() => navigate("/student-attendance")}
          className="inline-flex items-center gap-1.5 rounded-lg border border-brand-cyan/30 bg-brand-cyan/10 px-3 py-1.5 text-xs font-semibold text-cyan-200 hover:bg-brand-cyan/20"
        >
          <ClipboardCheck size={14} />
          Attendance
        </button>
        <button
          type="button"
          onClick={() => navigate("/admission-batches")}
          className="inline-flex items-center gap-1.5 rounded-lg border border-brand-violet/30 bg-brand-violet/10 px-3 py-1.5 text-xs font-semibold text-violet-200 hover:bg-brand-violet/20"
        >
          <Layers size={14} />
          Batches
        </button>
        <button
          type="button"
          onClick={() => navigate("/add-admission")}
          className="inline-flex items-center gap-1.5 rounded-lg border border-brand-lime/30 bg-brand-lime/10 px-3 py-1.5 text-xs font-semibold text-lime-200 hover:bg-brand-lime/20"
        >
          <GraduationCap size={14} />
          Add Admission
        </button>
      </div>
    </section>
  );
}
